/**
 * MLS-DUAL-001 Phase 2 — MLS supporting documents (order files tagged
 * category=supporting_document + component=mls; visible in QC, auto-included
 * in final delivery). MLS-DOCS-COMPACT: rendered as a compact card in the
 * left MLS column (below the service navigation) so it is reachable from
 * every service; View opens the file, Delete honours backend permissions.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Paperclip, Upload, Loader2, FileText, Eye, Trash2 } from 'lucide-react';
import { MLS_SECTIONS, UTILITY_TYPES, SERVICE_TO_SECTION, ClassificationBadge } from '../files/fileClassification';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtSize = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function MlsSupportingDocs({ orderId, readOnly = false, onCountChange, activeService }) {
  const [files, setFiles] = useState([]);
  // MLS-DOC-CLASS-001: section defaults to the service being worked on; user can override before upload
  const [section, setSection] = useState(SERVICE_TO_SECTION[activeService] || 'code_violations');
  const [utilityType, setUtilityType] = useState('');
  useEffect(() => { if (SERVICE_TO_SECTION[activeService]) setSection(SERVICE_TO_SECTION[activeService]); }, [activeService]);
  useEffect(() => { onCountChange?.(files.length); }, [files.length, onCountChange]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [highlightId, setHighlightId] = useState(null); // MLS-REVIEW-PACKAGE-002: 'Locate' from the review modal
  const inputRef = useRef(null);

  const loadFiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/orders/${orderId}/files`);
      const all = res.data?.files || res.data || [];
      setFiles(all.filter((f) => f.component === 'mls' && f.is_active !== false));
    } catch { /* silent — files tab still shows everything */ }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { loadFiles(); }, [loadFiles]);
  useEffect(() => {
    const onChanged = (e) => { if (!e.detail?.orderId || e.detail.orderId === orderId) loadFiles(); };
    const onLocate = (e) => {
      if (e.detail?.orderId && e.detail.orderId !== orderId) return;
      setHighlightId(e.detail?.fileId || null);
      setTimeout(() => document.querySelector(`[data-testid="mls-doc-item-${e.detail?.fileId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      setTimeout(() => setHighlightId(null), 6000);
    };
    window.addEventListener('mls-docs-changed', onChanged);
    window.addEventListener('mls-doc-locate', onLocate);
    return () => { window.removeEventListener('mls-docs-changed', onChanged); window.removeEventListener('mls-doc-locate', onLocate); };
  }, [orderId, loadFiles]);

  const upload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    for (const file of Array.from(fileList)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', 'supporting_document');
        fd.append('component', 'mls');
        fd.append('section', section);
        if (section === 'utility_accounts' && utilityType) fd.append('utility_type', utilityType);
        await axios.post(`${API}/api/orders/${orderId}/files/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(`Uploaded ${file.name}`);
      } catch (e) {
        const detail = e.response?.data?.detail;
        toast.error(`${file.name}: ${typeof detail === 'string' ? detail : detail?.message || 'upload failed'}`, { duration: 6000 });
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
    loadFiles();
  };

  const view = async (f) => {
    setBusyId(f.id);
    try {
      const res = await axios.get(`${API}/api/orders/${orderId}/files/${f.id}/download`, { responseType: 'blob' });
      window.open(URL.createObjectURL(res.data), '_blank');
    } catch { toast.error('Unable to open this document'); }
    finally { setBusyId(null); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.display_name || f.filename}"?`)) return;
    setBusyId(f.id);
    try {
      await axios.delete(`${API}/api/orders/${orderId}/files/${f.id}`);
      toast.success('Document deleted');
      loadFiles();
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'You are not permitted to delete documents' : (e.response?.data?.detail || 'Delete failed'));
    } finally { setBusyId(null); }
  };

  const iconBtn = 'h-6 w-6 grid place-items-center rounded text-slate-400 hover:bg-slate-100 transition-colors disabled:opacity-40';

  return (
    <div className="px-1" data-testid="mls-supporting-docs">
      <div className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-800 whitespace-nowrap">
        <span className="mls-tile bg-indigo-50 text-indigo-600" style={{ width: 24, height: 24, borderRadius: 7 }}><Paperclip className="w-3.5 h-3.5" /></span>Supporting Documents
        <span className="ml-auto normal-case tracking-normal font-semibold text-slate-500" data-testid="mls-doc-count">
          {loading ? '…' : `${files.length} ${files.length === 1 ? 'file' : 'files'}`}
        </span>
      </div>
      {!readOnly && (
        <>
          <div className="mt-1.5 flex gap-1">
            <select value={section} onChange={(e) => { setSection(e.target.value); setUtilityType(''); }} aria-label="MLS section for upload"
              className="h-6 flex-1 min-w-0 rounded border border-slate-200 bg-white px-1 text-[10.5px] text-slate-700" data-testid="mls-doc-section-select">
              {MLS_SECTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            {section === 'utility_accounts' && (
              <select value={utilityType} onChange={(e) => setUtilityType(e.target.value)} aria-label="Utility type"
                className="h-6 w-20 rounded border border-slate-200 bg-white px-1 text-[10.5px] text-slate-700" data-testid="mls-doc-utility-select">
                <option value="">Type…</option>
                {UTILITY_TYPES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            )}
          </div>
          <input ref={inputRef} type="file" multiple className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv"
            data-testid="mls-doc-file-input" onChange={(e) => upload(e.target.files)} />
          <button type="button" disabled={uploading} onClick={() => inputRef.current?.click()}
            className="mt-1.5 w-full inline-flex items-center justify-center gap-1 h-7 rounded-md border border-blue-200 bg-white text-[11px] font-semibold text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-50"
            data-testid="mls-doc-upload-btn">
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}Upload
          </button>
        </>
      )}
      {!loading && files.length === 0 && (
        <div className="mt-1.5 text-[10.5px] text-slate-400 leading-snug" data-testid="mls-doc-empty">No documents yet. PDF, Word, Excel, CSV.</div>
      )}
      {files.length > 0 && (
        <ul className="mt-1.5 space-y-1" data-testid="mls-doc-list">
          {files.map((f) => (
            <li key={f.id} className={`mls-doc-row flex items-center gap-1.5 rounded-md border bg-white px-1.5 py-1 ${highlightId === f.id ? 'border-rose-400 ring-2 ring-rose-300 bg-rose-50' : 'border-slate-200'}`} data-testid={`mls-doc-item-${f.id}`} data-highlighted={highlightId === f.id}>
              <FileText className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] text-slate-800 truncate" title={f.display_name || f.filename}>{f.display_name || f.filename}</div>
                <div className="text-[9.5px] text-slate-400 truncate">{fmtSize(f.file_size)}{f.uploaded_by ? ` · ${f.uploaded_by}` : ''}</div>
                <ClassificationBadge file={f} className="mt-0.5 !text-[9px] !px-1.5" testId={`mls-doc-class-${f.id}`} />
              </div>
              <button type="button" onClick={() => view(f)} disabled={busyId === f.id} className={`${iconBtn} hover:text-blue-700`} title="View" data-testid={`mls-doc-view-${f.id}`}>
                {busyId === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              {!readOnly && (
                <button type="button" onClick={() => remove(f)} disabled={busyId === f.id} className={`${iconBtn} hover:text-red-600`} title="Delete" data-testid={`mls-doc-delete-${f.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
